/**
 * 聊天区的附件：表情面板、@候选列表、带 @高亮的消息渲染。
 * 纯展示组件，规则都在 lib/chatText.ts 里。
 */

import { useEffect, useRef } from 'react'

import { CHAT_EMOJIS, tokenize } from '../lib/chatText'

/** 表情面板：点一个就回调并关闭；点外面或按 Esc 也关闭。 */
export function EmojiPicker({ onPick, onClose }: { onPick: (e: string) => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    // 延迟一拍再挂监听，否则打开它的那次点击会立刻把它关掉。
    const t = window.setTimeout(() => {
      document.addEventListener('mousedown', onDown)
      document.addEventListener('keydown', onKey)
    }, 0)
    return () => {
      window.clearTimeout(t)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [onClose])
  return (
    <div className="chat-emoji-pop" ref={ref} role="listbox" aria-label="表情">
      {CHAT_EMOJIS.map((e) => (
        <button key={e} type="button" className="chat-emoji-item" onClick={() => onPick(e)}>
          {e}
        </button>
      ))}
    </div>
  )
}

/** @候选列表。键盘上下/回车由父级处理，这里只负责画和点选。 */
export function MentionPop({
  names, active, onPick,
}: { names: string[]; active: number; onPick: (name: string) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  // 高亮项跟着键盘滚进视野。
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('.mention-item.on')?.scrollIntoView({ block: 'nearest' })
  }, [active])
  if (names.length === 0) return null
  return (
    <div className="mention-pop" ref={ref} role="listbox" aria-label="提醒谁">
      {names.map((n, i) => (
        <button
          key={n}
          type="button"
          className={`mention-item${i === active ? ' on' : ''}`}
          // mousedown 会让 textarea 失焦并把候选关掉，所以拦在 mousedown 上。
          onMouseDown={(e) => { e.preventDefault(); onPick(n) }}
        >
          @{n}
        </button>
      ))}
    </div>
  )
}

/** 正文渲染：@在场成员 高亮，@我 再加一层强调。 */
export function RichText({ text, names, myName }: { text: string; names: Iterable<string>; myName: string }) {
  if (!text.includes('@')) return <>{text}</>
  return (
    <>
      {tokenize(text, names).map((s, i) =>
        s.type === 'mention' ? (
          <span key={i} className={`mention${s.value === myName || s.value === '所有人' ? ' me' : ''}`}>
            @{s.value}
          </span>
        ) : (
          <span key={i}>{s.value}</span>
        ),
      )}
    </>
  )
}
